import { useFormContext } from "react-hook-form";
import { Icon, Skeleton, Typography } from "@/components/atoms";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import { clsx } from "@/utils";
import { VehicleTypeFormData } from "./form";
import { getCategoryIllustration } from "./categoryIllustrations";

interface Props {
  vehicleTypeName?: string;
}

export const CategoryPicker = ({ vehicleTypeName }: Props) => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<VehicleTypeFormData>();
  const selectedUuid = watch("vehicleCategoryUuid");
  const { data, isFetching } = useGetVehicleFormOptionsQuery();

  if (isFetching) return <Skeleton />;

  const categories = data?.vehicleCategories ?? [];
  if (categories.length === 0) {
    return (
      <Typography className="text-base text-danger">
        Vehicle categories aren&apos;t available right now. Try again.
      </Typography>
    );
  }

  const selectedCategory = categories.find((c) => c.uuid === selectedUuid);
  const displayName = vehicleTypeName?.trim() || "this vehicle type";

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-3 gap-2.5">
        {categories.map((cat) => {
          const isSelected = selectedUuid === cat.uuid;
          const Illustration = getCategoryIllustration(cat);
          return (
            <button
              key={cat.uuid}
              type="button"
              onClick={() => setValue("vehicleCategoryUuid", cat.uuid, { shouldDirty: true, shouldValidate: true })}
              className="flex flex-col items-center gap-2"
            >
              <div
                className={clsx(
                  "flex h-[120px] w-full items-center justify-center overflow-hidden rounded-lg border transition",
                  isSelected
                    ? "border-primary-dark bg-primary-light text-primary-dark"
                    : "border-neutral-mid-gray bg-neutral-surface-gray text-neutral-dark-gray hover:border-primary/40"
                )}
              >
                <Illustration aria-hidden className="h-auto max-h-[60px] w-auto max-w-[160px]" />
              </div>
              <Typography className="text-base font-semibold leading-5 text-neutral-black">{cat.name}</Typography>
            </button>
          );
        })}
      </div>

      {selectedCategory && (
        <div className="flex w-full items-start gap-2 rounded-lg border border-info bg-info-light px-2 py-1">
          <Icon name="InfoCircle" size="sm" className="mt-0.5 shrink-0 text-info" />
          <Typography className="text-base font-semibold leading-5 text-info-dark">
            When offloading a job outside your fleet, the system will automatically offload &ldquo;{displayName}&rdquo; jobs to{" "}
            {selectedCategory.name} in the network.
          </Typography>
        </div>
      )}

      {errors.vehicleCategoryUuid && (
        <Typography className="text-base text-danger">{errors.vehicleCategoryUuid.message}</Typography>
      )}
    </div>
  );
};
